import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Pencil, Trash2, Plus } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const PLANTILLA_VACIA = {
  nombre: "",
  slug: "",
  asunto: "",
  titulo: "",
  cuerpo: "",
  ctaTexto: "",
  ctaUrl: "",
};

const EmailTemplateManager = ({ onClose, onChanged }) => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [plantillas, setPlantillas] = useState([]);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(PLANTILLA_VACIA);
  const [guardando, setGuardando] = useState(false);

  const cargar = () => {
    axios
      .get(`${API}/admin/plantillas-email`, { headers })
      .then((res) => setPlantillas(res.data))
      .catch(() => toast.error("No se pudieron cargar las plantillas"));
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seleccionarParaEditar = (plantilla) => {
    setEditando(plantilla._id);
    setForm({
      nombre: plantilla.nombre,
      slug: plantilla.slug,
      asunto: plantilla.asunto,
      titulo: plantilla.titulo,
      cuerpo: plantilla.cuerpo,
      ctaTexto: plantilla.ctaTexto || "",
      ctaUrl: plantilla.ctaUrl || "",
    });
  };

  const nuevaPlantilla = () => {
    setEditando("nueva");
    setForm(PLANTILLA_VACIA);
  };

  const cancelarEdicion = () => {
    setEditando(null);
    setForm(PLANTILLA_VACIA);
  };

  const guardar = async () => {
    if (
      !form.nombre.trim() ||
      !form.slug.trim() ||
      !form.asunto.trim() ||
      !form.titulo.trim() ||
      !form.cuerpo.trim()
    ) {
      toast.error("Nombre, slug, asunto, título y cuerpo son obligatorios");
      return;
    }

    setGuardando(true);
    try {
      if (editando === "nueva") {
        await axios.post(`${API}/admin/plantillas-email`, form, { headers });
        toast.success("Plantilla creada");
      } else {
        await axios.put(`${API}/admin/plantillas-email/${editando}`, form, { headers });
        toast.success("Plantilla actualizada");
      }
      cancelarEdicion();
      cargar();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar la plantilla");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API}/admin/plantillas-email/${id}`, { headers });
      toast.success("Plantilla eliminada");
      cargar();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al eliminar la plantilla");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Plantillas de email</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {editando === null ? (
            <>
              <button
                onClick={nuevaPlantilla}
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <Plus className="h-4 w-4" /> Nueva plantilla
              </button>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {plantillas.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">
                    No hay plantillas creadas
                  </p>
                ) : (
                  plantillas.map((p) => (
                    <div key={p._id} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{p.nombre}</p>
                        <p className="text-xs text-gray-500 truncate">{p.asunto}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => seleccionarParaEditar(p)}
                          className="p-1.5 text-gray-500 hover:text-blue-600"
                          aria-label="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => eliminar(p._id)}
                          className="p-1.5 text-gray-500 hover:text-red-600"
                          aria-label="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Slug (ej: clientes-inactivos)"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Asunto del correo"
                value={form.asunto}
                onChange={(e) => setForm({ ...form, asunto: e.target.value })}
              />
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Título dentro del correo"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
              <div>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Cuerpo del correo"
                  rows={6}
                  value={form.cuerpo}
                  onChange={(e) => setForm({ ...form, cuerpo: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {"Usa {{nombre}} para insertar el nombre del destinatario. Deja una línea en blanco para separar párrafos."}
                </p>
              </div>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Texto del botón (opcional)"
                value={form.ctaTexto}
                onChange={(e) => setForm({ ...form, ctaTexto: e.target.value })}
              />
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="URL del botón (opcional)"
                value={form.ctaUrl}
                onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
              />

              <div className="flex items-center gap-2">
                <button
                  onClick={guardar}
                  disabled={guardando}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  {guardando ? "Guardando..." : "Guardar"}
                </button>
                <button onClick={cancelarEdicion} className="text-sm text-gray-600 hover:underline">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateManager;
